import React, { useState, useEffect } from 'react';
import { 
  Button, 
  Table, 
  Modal, 
  Form, 
  Alert, 
  Spinner, 
  Row, 
  Col, 
  Card 
} from 'react-bootstrap';
import { FaRegEdit } from "react-icons/fa";
import { FaRegTrashCan } from "react-icons/fa6";
import axios from '../config/api';
import './AIFeedback.css';

const AIFeedback = () => {
  const [feedbackList, setFeedbackList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingFeedback, setEditingFeedback] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    question: '',
    wrong_answer: '',
    right_answer: '',
    brand_name: ''
  });

  const brandOptions = [
    { value: '', label: 'Select Brand' },
    { value: 'Iconinc', label: 'Iconinc' },
    { value: 'Rentinc', label: 'Rentinc' },
    { value: 'Parklane Properties', label: 'Parklane Properties' },
    { value: 'Hive', label: 'Hive' }
  ];

  useEffect(() => {
    fetchFeedbackList();
  }, []);

  const fetchFeedbackList = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get('/api/ai-feedback/');
      setFeedbackList(response.data);
    } catch (error) {
      console.error('Error fetching AI feedback:', error);
      setError('Failed to fetch AI feedback: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      if (editingFeedback) {
        await axios.put(`/api/ai-feedback/${editingFeedback.id}`, formData);
        setSuccess('AI Feedback updated successfully');
      } else {
        await axios.post('/api/ai-feedback/', formData);
        setSuccess('AI Feedback created successfully');
      }
      setShowModal(false);
      setEditingFeedback(null);
      setFormData({
        question: '',
        wrong_answer: '',
        right_answer: '',
        brand_name: ''
      });
      fetchFeedbackList();
    } catch (error) {
      console.error('Error saving AI feedback:', error);
      setError('Failed to save AI feedback: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEdit = (feedback) => {
    setEditingFeedback(feedback);
    setFormData({
      question: feedback.question,
      wrong_answer: feedback.wrong_answer,
      right_answer: feedback.right_answer,
      brand_name: feedback.brand_name || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this feedback?')) {
      try {
        await axios.delete(`/api/ai-feedback/${id}`);
        setSuccess('AI Feedback deleted successfully');
        fetchFeedbackList();
      } catch (error) {
        console.error('Error deleting AI feedback:', error);
        setError('Failed to delete AI feedback: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  const openCreateModal = () => {
    setEditingFeedback(null);
    setFormData({
      question: '',
      wrong_answer: '',
      right_answer: '',
      brand_name: ''
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingFeedback(null);
    setFormData({
      question: '',
      wrong_answer: '',
      right_answer: '',
      brand_name: ''
    });
    setError('');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading AI Feedback...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-layout">
      <div className="page-header">
        <Row>
          <Col>
            <h2>AI Feedback Management</h2>
            <p className="text-muted mb-0">
              Manage question-answer feedback for AI training
            </p>
          </Col>
        </Row>
      </div>

      <div className="page-content">
        {error && (
          <Alert variant="danger" onClose={() => setError('')} dismissible>
            {error}
          </Alert>
        )}

        {success && (
          <Alert variant="success" onClose={() => setSuccess('')} dismissible>
            {success}
          </Alert>
        )}

        <Card>
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">
                AI Feedback ({feedbackList.length})
              </h5>
              <div>
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  onClick={openCreateModal}
                >
                  Add New Feedback
                </Button>
              </div>
            </div>

            <Table responsive hover>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Question</th>
                  <th>Wrong Answer</th>
                  <th>Right Answer</th>
                  <th>Brand</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {feedbackList.length > 0 ? (
                  feedbackList.map((feedback) => (
                    <tr key={feedback.id}>
                      <td>
                        <code>{feedback.id}</code>
                      </td>
                      <td>
                        <div className="question-text">{feedback.question}</div>
                      </td>
                      <td>
                        <div className="wrong-answer-text">{feedback.wrong_answer}</div>
                      </td>
                      <td>
                        <div className="right-answer-text">{feedback.right_answer}</div>
                      </td>
                      <td>
                        <span className="brand-badge">{feedback.brand_name || 'General'}</span>
                      </td>
                      <td>{formatDate(feedback.created_at)}</td>
                      <td>
                        <Button
                          className="action-btn me-2"
                          size="sm"
                          variant="outline"
                          onClick={() => handleEdit(feedback)}
                        >
                          <FaRegEdit />
                        </Button>
                        <Button
                          variant="outline"
                          className="action-btn"
                          size="sm"
                          onClick={() => handleDelete(feedback.id)}
                        >
                          <FaRegTrashCan />
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center py-4">
                      <div className="text-muted">
                        <i className="fas fa-question-circle mb-2" style={{ fontSize: '2rem' }}></i>
                        <br />
                        No AI feedback found
                        <br />
                        <small>Start by adding your first feedback</small>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      </div>

      {/* Modal */}
      {showModal && (
        <Modal show={showModal} onHide={closeModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              {editingFeedback ? 'Edit AI Feedback' : 'Add New AI Feedback'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Question *</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="question"
                  value={formData.question}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter the question..."
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Wrong Answer *</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="wrong_answer"
                  value={formData.wrong_answer}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter the incorrect answer..."
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Right Answer *</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="right_answer"
                  value={formData.right_answer}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter the correct answer..."
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Brand Name</Form.Label>
                <Form.Select
                  name="brand_name"
                  value={formData.brand_name}
                  onChange={handleInputChange}
                >
                  {brandOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeModal}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSubmit}>
              {editingFeedback ? 'Update Feedback' : 'Create Feedback'}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};

export default AIFeedback; 